<script setup lang="ts">
import { BoxComponent, FlexComponent, LoadingComponent, TypographyComponent } from '..'

withDefaults(
  defineProps<{
    title?: string
    description?: string
    isLoading?: boolean
    isEmpty?: boolean
    emptyText?: string
    /** `medium` is 24/32 (`headline-medium`); `small` is 20/28 (`headline-small`). */
    titleSize?: 'medium' | 'small'
  }>(),
  {
    title: '',
    description: '',
    isLoading: false,
    isEmpty: false,
    emptyText: '',
    titleSize: 'medium',
  },
)

defineSlots<{
  title?(): void
  headerEnd?(): void
  default?(): void
  footer?(): void
}>()

const LOADING_ICON_SIZE = 48
</script>

<template>
  <BoxComponent :p="24" class="ui-card">
    <FlexComponent direction="column" :gap="24">
      <FlexComponent v-if="title || description || $slots.title || $slots.headerEnd" direction="column" :gap="8">
        <FlexComponent justify="space-between" align="center" :gap="8">
          <slot name="title">
            <TypographyComponent v-if="title" :variant="`headline-${titleSize}`">{{ title }}</TypographyComponent>
          </slot>
          <FlexComponent v-if="$slots.headerEnd" align="center" :gap="4" class="ui-card__header-end">
            <slot name="headerEnd" />
          </FlexComponent>
        </FlexComponent>
        <TypographyComponent v-if="description" variant="body-medium" color="var(--secondary-48)">
          {{ description }}
        </TypographyComponent>
      </FlexComponent>

      <LoadingComponent v-if="isLoading" :size="LOADING_ICON_SIZE" class="ui-card__loading" />

      <BoxComponent v-else-if="isEmpty" :p="16" class="ui-card__empty">
        <TypographyComponent variant="body-medium" color="var(--secondary-48)" text-align="center">
          {{ emptyText }}
        </TypographyComponent>
      </BoxComponent>

      <FlexComponent v-else-if="$slots.default" direction="column" :gap="16" class="ui-card__body">
        <slot />
      </FlexComponent>

      <slot v-if="!isLoading" name="footer" />
    </FlexComponent>
  </BoxComponent>
</template>

<style scoped lang="scss">
// `ui-card`, not `card`: Bootstrap's global `.card` rule would otherwise style the root in advertiser-app.
.ui-card {
  border: 1px solid var(--button-hover);
  border-radius: 16px;
  background: var(--inverted);
}

.ui-card__header-end {
  white-space: nowrap;
}

.ui-card__loading {
  min-height: 200px;
}

.ui-card__empty {
  border-radius: 16px;
  white-space: pre-line;
  background: var(--item-hover);
}
</style>
